<template>
  <div class="view-wrapper">
    <!-- Floating Controls -->
    <div class="size-controls">
      <!-- Regenerate Button -->
      <button
        class="regenerate-button"
        :disabled="isRegenerating"
        @click="regenerateInsights"
      >
        <span v-if="isRegenerating" class="spinner"></span>
        <span>{{ isRegenerating ? "Regenerating..." : "Regenerate" }}</span>
      </button>
    </div>

    <!-- Loading / Waiting for data state (Stays active until data exists) -->
    <div v-if="isLoading || isRegenerating || !hasData" class="loading-state">
      <div class="loading-content">
        <span class="modern-spinner"></span>
        <p class="loading-text">
          {{ isRegenerating ? "Regenerating insights..." : "Loading insights..." }}
        </p>
      </div>
    </div>

    <!-- Tags Grid -->
    <div v-else class="tagsContainer">
      <div
        style="margin: 3rem"
        v-for="(type, key) in aiThemeTags"
        :key="key"
      >
        <DisplayTagsVue
          @click="goToEmails(type.tag)"
          :size="tagSize"
          :tags="[
            {
              type: type.tag,
              custom: type.custom,
              emoji: type.desc,
              desc: type.count,
            },
          ]"
          :hoverAndClickStyling="true"
          :percentage-mode="true"
          :grand-total="globalGrandTotal"
          titleClass="insights-caption-override"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import DisplayTagsVue from "./DisplayTags.vue";

const store = useStore();
const router = useRouter();

const isLoading = ref(true);
const isRegenerating = ref(false);

const aiThemeTags = computed(() => store.getters.aiThemeTags || {});

// Evaluates whether data is populated (array or object with items)
const hasData = computed(() => {
  const data = aiThemeTags.value;
  if (!data) return false;
  return Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0;
});

// Single global grand total calculation across extractedTags
const globalGrandTotal = computed(() => {
  const data = aiThemeTags.value; // <-- fixed from extractedTags
  if (!data) return 0;
  const items = Array.isArray(data) ? data : Object.values(data);
  const numericSum = items.reduce((sum, item) => {
    const val = Number(item.count ?? item.desc);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  return numericSum > 0 ? numericSum : items.length;
});

// Size constraints
const tagSize = ref(160);

onMounted(async () => {
  isLoading.value = true;
  try {
    await store.dispatch("aiThemeTags");
  } catch (error) {
    console.error("Error loading insights:", error);
  } finally {
    isLoading.value = false;
  }
});

async function regenerateInsights() {
  if (isRegenerating.value) return;

  isRegenerating.value = true;
  try {
    await store.dispatch("aiThemeTagsGenerate");
    await nextTick();
  } catch (error) {
    console.error("Error regenerating insights:", error);
  } finally {
    isRegenerating.value = false;
  }
}

function goToEmails(type) {
  store.commit("tagsFilter", "");
  store.commit("clearSearchResults");
  store.commit("setSearchText", type.toLowerCase());
  router.push("emailkb");
}
</script>

<style scoped>
.view-wrapper {
  position: relative;
  width: 100%;
}

/* Floating controls */
.size-controls {
  position: fixed;
  bottom: 1rem;
  left: 1.5rem;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  user-select: none;
  z-index: 9999;
}

/* Regenerate button */
.regenerate-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 105px;
  height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: #b86200;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease, transform 0.1s ease;
}

.regenerate-button:hover:not(:disabled) {
  background: #994f00;
}

.regenerate-button:active:not(:disabled) {
  transform: scale(0.97);
}

.regenerate-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Small spinner inside button */
.spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* Full loading state */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

/* Modern loading spinner */
.modern-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(184, 98, 0, 0.15);
  border-top-color: #b86200;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.loading-text {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.01em;
  margin: 0;
}

/* Tags */
.tagsContainer {
  display: flex;
  flex-wrap: wrap;
}

:deep(.insights-caption-override) {
  max-width: 300px;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: break-word;
  font-size: 16px;
  line-height: 1.5;
  text-align: left;
  padding: 10px 14px;
  background-color: #f8fafc;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>